import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Data } from '@angular/router';

import { ConfiguracaoCaixaApiService } from '../../services/configuracao-caixa-api.service';
import { AddContaCaixaDialogComponent } from '../dialogs/add-conta-caixa-dialog/add-conta-caixa-dialog.component';
import { MessageService } from '@services/message.service';
import { ConfiguracaoCaixaComponent } from '../configuracao-caixa.component';
import { ContaCaixaConfiguracao } from '../../models/conta-caixa-configuracao';
import { ContaBancoDialogComponent } from '../dialogs/conta-banco-dialog/conta-banco-dialog.component';
import { MesclarContasDialogComponent } from '../dialogs/mesclar-contas-dialog/mesclar-contas-dialog.component';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';
import { AcaoContaMenu } from '../../models/acao-conta-menu';
import { Permissao } from '@models/enums/permissao';
import { TravaContaCaixaDialogComponent } from '../dialogs/trava-conta-caixa-dialog/trava-conta-caixa-dialog.component';

@Component({
  selector: 'app-configuracao-caixa-ativas',
  standalone: false,
  templateUrl: './configuracao-caixa-ativas.component.html',
  styleUrl: './configuracao-caixa-ativas.component.scss'
})
export class ConfiguracaoCaixaAtivasComponent {
  protected get permissaoEditarLancamento() { return Permissao.EDITAR_LANCAMENTO_CAIXA; }

  excelFileType: string = ".xlsx";

  acoesConta: AcaoContaMenu[] = [
    {
      label: 'Mesclar contas',
      icon: 'pi-arrow-down-left-and-arrow-up-right-to-center',
      acao: (conta) => this.mesclarContasClick(conta)
    },
    {
      label: 'Vincular conta bancária',
      icon: 'pi-link',
      acao: (conta) => this.vincularContaBancoClick(conta),
      visivel: (conta) => !conta.contaBancoId
    },
    {
      label: 'Vincular conta ao cartão',
      icon: 'pi-credit-card',
      acao: (conta) => this.vincularContaCartaoClick(conta),
      visivel: (conta) => !conta.contaBancoId
    },
    {
      label: 'Trava da conta',
      icon: 'pi-lock',
      acao: (conta) => this.travaContaClick(conta),
      permissao: Permissao.TRAVAR_CONTA_CAIXA
    },
    {
      label: 'Arquivar conta',
      icon: 'pi-box',
      acao: (conta) => this.arquivarContaClick(conta),
      permissao: Permissao.ARQUIVAR_CONTA_CAIXA
    }
  ];

  contas: ContaCaixaConfiguracao[] = [];

  constructor(
    private route: ActivatedRoute,
    private configuracaoCaixaApiService: ConfiguracaoCaixaApiService,
    private messageService: MessageService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.route.data.subscribe((data: Data) => {
      this.contas = data['contas'];
    });
  }

  public reloadPage(): void {
    this.configuracaoCaixaApiService.getContasCaixa().subscribe((contas: ContaCaixaConfiguracao[]) => {
      this.contas = contas;
    });
  }

  public abrirAdicionarContaDialog(): void {
    const dialogRef = this.dialog.open(AddContaCaixaDialogComponent, {
      width: '450px',
      panelClass: 'no-padding-dialog',
      disableClose: true
    });
    dialogRef.componentInstance.title = "Criar conta caixa";

    dialogRef.afterClosed().subscribe((salvo: boolean | undefined) => {
      if (salvo)
        this.reloadPage();
    });
  }

  public importarLancamentosCaixa(inputElement: HTMLInputElement): void {
    const file: File | null = inputElement.files === null ? null : inputElement.files[0];
    inputElement.value = '';

    if (!file) return;

    this.configuracaoCaixaApiService.importarContasCaixa(file).subscribe({
      next: () => {
        this.messageService.showToastSuccess("importação realizada com sucesso.");
      }
    });
  }

  private vincularContaBancoClick(conta: ContaCaixaConfiguracao): void {
    this.abrirVincularContaBancoDialog(conta, false);
  }

  private vincularContaCartaoClick(conta: ContaCaixaConfiguracao): void {
    this.abrirVincularContaBancoDialog(conta, true);
  }

  private abrirVincularContaBancoDialog(conta: ContaCaixaConfiguracao, isCartao: boolean): void {
    const dialogRef = this.dialog.open(ContaBancoDialogComponent, {
      width: '580px',
      panelClass: 'no-padding-dialog',
      disableClose: true
    });

    dialogRef.componentInstance.contaCaixaId = conta.id;
    dialogRef.componentInstance.isCartao = isCartao;

    dialogRef.afterClosed().subscribe((salvo: boolean | undefined) => {
      if (salvo)
        this.reloadPage();
    });
  }

  private mesclarContasClick(conta: ContaCaixaConfiguracao): void {
    const dialogRef = this.dialog.open(MesclarContasDialogComponent, {
      width: '720px',
      panelClass: 'no-padding-dialog',
      disableClose: true
    });

    dialogRef.componentInstance.contas = this.contas;
    dialogRef.componentInstance.contaAtual = conta;

    dialogRef.afterClosed().subscribe((salvo: boolean | undefined) => {
      if (salvo)
        this.reloadPage();
    });
  }

  private travaContaClick(conta: ContaCaixaConfiguracao): void {
    const dialogRef = this.dialog.open(TravaContaCaixaDialogComponent, {
      width: '450px',
      panelClass: 'no-padding-dialog',
      disableClose: true
    });
    dialogRef.componentInstance.conta = conta;
  }

  private arquivarContaClick(conta: ContaCaixaConfiguracao): void {
    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true
    });
    dialogRef.componentInstance.titulo = `Arquivar conta`;
    dialogRef.componentInstance.mensagem = `Essa conta deixará de ser exibida em listagens`;
    dialogRef.componentInstance.textoBtnConfirmacao = "Arquivar";
    dialogRef.componentInstance.btnBackgroundColor = "#F44336";
    dialogRef.componentInstance.acaoConfirmacao = () => this.arquivarConta(conta);
  }

  private arquivarConta(conta: ContaCaixaConfiguracao): void {
    this.configuracaoCaixaApiService.arquivarContaCaixa(conta.id).subscribe({
      next: () => {
        this.messageService.showToastSuccess("Conta arquivada com sucesso.");
        this.reloadPage();
      }
    });
  }
}
